import { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { TestResultsChips } from '@elementary/common-ui/components';
import useSelectedTreeItem from '@elementary/common-ui/hooks/useSelectedTreeItem';
import {
  TestResultsGroup,
  Totals,
  TotalTypes,
} from '@elementary/common-ui/types';
import useNavigateWithEnv from 'shared/hooks/useNavigateWithEnv';
import { groupBy } from '@elementary/common-ui/utils/common';

const TooltipText = styled.div`
  white-space: pre-wrap;
`;

type Props = {
  assetId: string;
  statuses?: TestResultsGroup;
  columnName?: string;
};

const TestResultsTableCell = ({ assetId, statuses, columnName }: Props) => {
  const navigate = useNavigateWithEnv();
  const { formatUrlParam } = useSelectedTreeItem();

  const getTooltipContent = useCallback(
    (status: keyof Totals) => {
      if (!statuses) {
        return '';
      }
      const values = statuses[status];
      const grouped = groupBy(values, (val) => val);
      return Object.keys(grouped)
        .map((group) => `${group} (${grouped[group].length})`)
        .join('\n');
    },
    [statuses],
  );

  const tooltips = useMemo(
    () => ({
      errors: <TooltipText>{getTooltipContent(TotalTypes.errors)}</TooltipText>,
      warnings: (
        <TooltipText>{getTooltipContent(TotalTypes.warnings)}</TooltipText>
      ),
      passed: <TooltipText>{getTooltipContent(TotalTypes.passed)}</TooltipText>,
      noData: <TooltipText>{getTooltipContent(TotalTypes.noData)}</TooltipText>,
      failures: (
        <TooltipText>{getTooltipContent(TotalTypes.failures)}</TooltipText>
      ),
    }),
    [getTooltipContent],
  );

  const totals: Totals | undefined = statuses
    ? {
        errors: statuses.errors.length,
        warnings: statuses.warnings.length,
        passed: statuses.passed.length,
        noData: statuses.noData.length,
        failures: statuses.failures.length,
      }
    : undefined;

  return (
    <TestResultsChips
      id={assetId}
      totals={totals}
      navigate={navigate}
      formatUrlParam={formatUrlParam}
      tooltips={tooltips}
      columnName={columnName}
    />
  );
};

export default TestResultsTableCell;
